import React from 'react';
import BranchMultiSelectAutoCompleteInput from 'screens/components/basic/BranchMultiSelectAutoCompleteInput';
import TextInput from 'screens/components/basic/TextInput';

/**
 * Implementing AutomationDrawer Component in Reporting File
 *
 * This guide describes how to add and configure the AutomationDrawer component to the report file.
 * Follow these steps to integrate the AutomationDrawer with your reporting functionality.
 */

// Step 1: Integrate AutomationDrawer Component
// --------------------------------------------
// Add the AutomationDrawer component to your report file.

// Step 2: Utilize the getAutomationButtons Function
// -------------------------------------------------
// Import the function named getAutomationButtons and use it within the render method.
// Declare the `drawerContent` constant as follows:
//
// const drawerContent = getAutomationButtons(reportName)(options);
//
// Parameters:
// - reportName (string): The name of the report, used to match the corresponding report fields.
// - options (object): Configuration options for the textField or multiSelect components etc...

// Step 3: Add Drawer Toggle Functionality
// ---------------------------------------
// Create a function named `toggleDrawer` that toggles the drawer's open state.
// This function should be passed to both the header button and the AutomationDrawer component.
//
// Example:
//  toggleDrawer = (open) => {
//   if (typeof open === 'boolean') {
//     this.setState({ isOpenDrawer: open });
//   } else {
//     this.setState((prevState) => ({ isOpenDrawer: !prevState.isOpenDrawer }));
//   }
// }

// Step 4: Define Automation Model
// -------------------------------
// Define a function called `automationModel` that returns an object representing the model for automation.
// Update the model properties as necessary based on your component's props.
//
// Example:
//  automationModel = () => {
//   const { AutomationBranchIds, AutomationEmail, loggedBranch } = this.props;
//   const model = {
//     Email: AutomationEmail,
//     BranchId: loggedBranch,
//     ReportBranchIds: AutomationBranchIds || [],
//     ReportId: 11,
//     APIUrl: "ReportEmailAutomation/Create"
//   };
//   return model;
// }

// Step 5: Set Component Props
// ---------------------------
// Configure the props to be passed to the AutomationDrawer component.
// - reportId: The corresponding report ID (e.g., 11).
// - reportModel: Reference to the `automationModel` function.
//
// Example:
// <AutomationDrawer name="End of Day Report" open={this.state.isOpenDrawer} toggleDrawer={this.toggleDrawer} drawerContent={drawerContent} reportModel={this.automationModel} reportId={11} />

const reportFields = {
  "EndOfDayReport": (props) => [
    <div key={props.reportName} className="row">
      <div className="col-xs-12 pa-1">
        <BranchMultiSelectAutoCompleteInput
          defaultValues={props.defaultBranch}
          name={`${props.reportName}AutomationLocations`}
          fullWidth={true}
          autoWidth={true}
          label="Locations"
          floatingLabelText="Select Location(s)"
          hintText="Select locations to display"
          style={{ fontFamily: "Open Sans, Arial, Helvetica, sans-serif", fontSize: "16px", letterSpacing: "-0.25px" }}
          options={props.branchOptions}
          fieldName={'Locations'}
        />
      </div>
      <div className="col-xs-12">
        <TextInput
        name={`${props.reportName}AutomationEmail`}
        floatingLabelText="Email"
        fullWidth={true}
        hintText="Enter Your Email Here"
        type="email"
        maxLength={50} />
      </div>
    </div>
  ],
  "IRCCReport": (props) => [
    <div key={props.reportName} className="row">
       <div className="col-xs-12 pa-1">
        <BranchMultiSelectAutoCompleteInput
          defaultValues={props.defaultBranch}
          name={`${props.reportName}AutomationLocations`}
          fullWidth={true}
          autoWidth={true}
          label="Locations"
          floatingLabelText="Select Location(s)"
          hintText="Select locations to display"
          style={{ fontFamily: "Open Sans, Arial, Helvetica, sans-serif", fontSize: "16px", letterSpacing: "-0.25px" }}
          options={props.branchOptions}
          fieldName={'Locations'}
        />
      </div>
      <div className="col-xs-12">
        <TextInput
        name={`${props.reportName}AutomationEmail`}
        floatingLabelText="Email"
        fullWidth={true}
        hintText="Enter Your Email Here"
        type="email"
        maxLength={50} />
      </div>
    </div>
  ]
};

export const getAutomationButtons = (reportName) => {
  const generateFields = reportFields[reportName];
  return (props) => {
    const nameAndProps = { reportName, ...props };
    return generateFields ? generateFields(nameAndProps) : null;
  };
};
